import type { ApiClient } from '@/api/client';
import { apiRoutes } from '@/api/routes';

/**
 * A photograph chosen during a registration, before the person it belongs to exists.
 *
 * **Nothing here reaches `createPersonRequestSchema.imageIds`, and that is deliberate.** No route in
 * this system can produce an image before its person: a photograph is uploaded to
 * `/persons/:id/images`, which needs an id the create has not minted, so `imageIds` is refused with
 * the path named whenever it carries anything. What the registration form does instead is hold the
 * bytes in this browser, create the record, and attach them to the id the server has just answered
 * with. The contract does not move an inch.
 *
 * **The two kinds are not interchangeable and must not be collapsed.** An upload is a file part and
 * a capture is base64 in a JSON body; the server records `capturedVia` from which route was used,
 * which is a fact an operator can see with their own eyes and which the print layout and the audit
 * trail both distinguish. So a staged photograph remembers which one it is rather than being
 * normalised into one shape here.
 */

/** One photograph held in the browser, waiting for a person to belong to. */
export type StagedPhotograph =
  | {
      readonly id: string;
      /** What an `<img src>` shows while it waits. Minted when the photograph is staged. */
      readonly previewSrc: string;
      readonly kind: 'file';
      readonly file: File;
    }
  | {
      readonly id: string;
      readonly previewSrc: string;
      readonly kind: 'capture';
      /** base64 with no data-URL prefix, exactly as the capture route demands. */
      readonly frame: string;
    };

/**
 * A local identity for one staged photograph.
 *
 * It exists so a partially-completed attach can drop exactly the photographs that landed. Not the
 * array index, which renumbers the moment one is removed, and not the `File` itself, because a
 * capture has none.
 */
function stagedId(): string {
  return globalThis.crypto.randomUUID();
}

/**
 * A chosen file, staged.
 *
 * **The object URL is minted HERE, in the handler that chose the file, never during a render.** A
 * `URL.createObjectURL` computed while rendering is a side effect in a pure function — which the
 * compiler rules this project runs at `error` refuse outright — and it also leaks one URL per
 * render for as long as the form is open.
 */
export function stageFile(file: File): StagedPhotograph {
  return { id: stagedId(), previewSrc: URL.createObjectURL(file), kind: 'file', file };
}

/** A captured frame, staged. Its preview needs no object URL and must never grow one. */
export function stageCapture(frame: string): StagedPhotograph {
  return {
    id: stagedId(),
    previewSrc: `data:image/jpeg;base64,${frame}`,
    kind: 'capture',
    frame,
  };
}

/**
 * Gives back whatever a staged photograph borrowed from the platform.
 *
 * Only the `file` branch borrows anything; a `data:` URL is a string and revoking one is a no-op
 * that reads as though it mattered. **`person-form.tsx` is the single owner of this call**: if the
 * strip revoked on removal and the form also revoked on unmount, a preview would be released twice
 * or, when the form replaced the array without telling the strip, never.
 */
export function releaseStaged(one: StagedPhotograph): void {
  if (one.kind === 'file') {
    URL.revokeObjectURL(one.previewSrc);
  }
}

/**
 * Attaches staged photographs to a record that now exists, one at a time.
 *
 * **Sequential rather than concurrent**, because `nextImagePosition` takes the person's row lock to
 * choose the lowest free slot: two uploads racing for it is contention on the write path for no
 * gain at a desk where there is one photograph, and it makes the order the positions come out in a
 * property of the network rather than of what the operator chose.
 *
 * **`onAttached` is called after each one commits, and the caller is expected to forget it.**
 * Neither image route is behind the idempotency middleware — a photograph's bytes would digest
 * differently on every genuinely distinct upload — so a retry that re-sent the whole list would
 * store a second copy of every photograph that had already landed, burning slots out of the
 * five-per-person cap with nothing on screen to say which thumbnail is the duplicate.
 */
export async function attachStagedPhotographs(
  client: ApiClient,
  personId: string,
  staged: readonly StagedPhotograph[],
  onAttached: (one: StagedPhotograph) => void,
): Promise<void> {
  for (const one of staged) {
    if (one.kind === 'file') {
      const form = new FormData();
      // `file` is the one part name the server's multipart reader accepts; anything else is
      // refused rather than ignored.
      form.append('file', one.file, one.file.name);
      await client.request(apiRoutes.personImageUpload, { params: { id: personId }, form });
    } else {
      await client.request(apiRoutes.personImageCapture, {
        params: { id: personId },
        body: { frame: one.frame },
      });
    }
    onAttached(one);
  }
}
